/**
 * DrawingToolService - Manages drawing state and OpenLayers interactions
 *
 * Provides methods for creating, modifying, and managing temporary drawings
 * and text annotations on the map using OpenLayers Draw, Modify, and
 * Translate interactions.
 *
 * Validates: Requirements 39.1, 39.2, 39.4, 39.5, 39.6, 40.1, 40.2, 40.3, 40.5, 40.6
 */
import Map from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Draw, { createBox } from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import Translate from 'ol/interaction/Translate';
import Feature from 'ol/Feature';
import { Circle as CircleGeom, Geometry, Point, LineString, Polygon } from 'ol/geom';
import { fromCircle } from 'ol/geom/Polygon';
import { Style, Fill, Stroke, Circle as CircleStyle, Text as OlText } from 'ol/style';
import { GeoJSON as GeoJSONFormat } from 'ol/format';
import type {
  Geometry as GeoJSONGeometry,
  Feature as GeoJSONFeature,
  FeatureCollection,
  Point as GeoJSONPoint,
} from 'geojson';
import type { Drawing, DrawingStyle, DrawingToolType, TextStyle } from '../types/drawing';
import { DEFAULT_DRAWING_STYLE, DEFAULT_TEXT_STYLE } from '../types/drawing';

/** Generate a unique ID using crypto.randomUUID or fallback */
function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DrawingEventCallback = (drawing: Drawing) => void;
export type DrawingChangedCallback = (drawing: Drawing) => void;
export type TextPlacementCallback = (coords: [number, number]) => void;

// ---------------------------------------------------------------------------
// Service class
// ---------------------------------------------------------------------------

export class DrawingToolService {
  private map: Map | null = null;
  private drawingLayer: VectorLayer<VectorSource> | null = null;
  private drawingSource: VectorSource | null = null;
  private drawInteraction: Draw | null = null;
  private modifyInteraction: Modify | null = null;
  private translateInteraction: Translate | null = null;
  private currentStyle: DrawingStyle = { ...DEFAULT_DRAWING_STYLE };
  private currentTextStyle: TextStyle = { ...DEFAULT_TEXT_STYLE };
  private onDrawingAdded: DrawingEventCallback | null = null;
  private onDrawingChanged: DrawingChangedCallback | null = null;
  private onTextPlacement: TextPlacementCallback | null = null;
  private geoJsonFormat: GeoJSONFormat;

  constructor() {
    this.geoJsonFormat = new GeoJSONFormat();
  }

  // ─── Initialization ──────────────────────────────────────────────────────

  /**
   * Initialize the drawing service with a map instance.
   * Creates the temporary drawing layer (VectorLayer with VectorSource).
   */
  initialize(map: Map): void {
    this.map = map;
    this.drawingSource = new VectorSource();
    this.drawingLayer = new VectorLayer({
      source: this.drawingSource,
      style: (feature) => this.getFeatureStyle(feature as Feature),
      zIndex: 9999, // Always on top
    });
    this.drawingLayer.set('name', 'drawing-layer');
    this.drawingLayer.set('isDrawingLayer', true);
    this.map.addLayer(this.drawingLayer);
  }

  /**
   * Clean up all interactions and remove the drawing layer.
   */
  dispose(): void {
    this.deactivateAllInteractions();
    if (this.map && this.drawingLayer) {
      this.map.removeLayer(this.drawingLayer);
    }
    this.drawingLayer = null;
    this.drawingSource = null;
    this.map = null;
  }

  // ─── Event Callbacks ─────────────────────────────────────────────────────

  setOnDrawingAdded(callback: DrawingEventCallback | null): void {
    this.onDrawingAdded = callback;
  }

  setOnDrawingChanged(callback: DrawingChangedCallback | null): void {
    this.onDrawingChanged = callback;
  }

  setOnTextPlacement(callback: TextPlacementCallback | null): void {
    this.onTextPlacement = callback;
  }

  // ─── Style Management ────────────────────────────────────────────────────

  setDrawingStyle(style: DrawingStyle): void {
    this.currentStyle = { ...style };
    // Refresh existing features
    this.drawingSource?.changed();
  }

  setTextStyle(style: TextStyle): void {
    this.currentTextStyle = { ...style };
    this.drawingSource?.changed();
  }

  // ─── Tool Activation ─────────────────────────────────────────────────────

  /**
   * Activate a drawing tool. Deactivates any previously active tool.
   */
  activateTool(toolType: DrawingToolType): void {
    this.deactivateAllInteractions();

    if (!toolType || !this.map || !this.drawingSource) return;

    if (toolType === 'text') {
      this.activateTextTool();
      return;
    }

    const drawOptions = this.getDrawOptions(toolType);
    if (!drawOptions) return;

    this.drawInteraction = new Draw({
      source: this.drawingSource,
      ...drawOptions,
    });

    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      const drawingId = generateId();
      const createdAt = Date.now();
      feature.setId(drawingId);
      feature.set('drawingType', toolType);
      feature.set('drawingStyle', { ...this.currentStyle });
      feature.set('drawingId', drawingId);
      feature.set('createdAt', createdAt);

      const geometry = feature.getGeometry();
      if (!geometry) return;

      // Convert circle geometry to polygon for GeoJSON serialization
      let geojsonGeometry: GeoJSONGeometry;
      if (geometry instanceof CircleGeom) {
        const polygonGeom = fromCircle(geometry, 64);
        feature.setGeometry(polygonGeom);
        geojsonGeometry = JSON.parse(
          this.geoJsonFormat.writeGeometry(polygonGeom, {
            dataProjection: 'EPSG:4326',
            featureProjection: this.map!.getView().getProjection(),
          })
        );
      } else {
        geojsonGeometry = JSON.parse(
          this.geoJsonFormat.writeGeometry(geometry, {
            dataProjection: 'EPSG:4326',
            featureProjection: this.map!.getView().getProjection(),
          })
        );
      }

      const drawing: Drawing = {
        id: drawingId,
        type: toolType,
        geometry: geojsonGeometry,
        style: { ...this.currentStyle },
        createdAt,
      };

      this.onDrawingAdded?.(drawing);
    });

    this.map.addInteraction(this.drawInteraction);
  }

  /**
   * Activate text annotation tool - listens for map clicks.
   */
  private activateTextTool(): void {
    if (!this.map) return;

    const clickHandler = (event: import('ol/MapBrowserEvent').default<UIEvent>) => {
      const coords = this.map!.getCoordinateAtPixel(event.pixel);
      if (!coords) return;

      // Convert to lon/lat
      const lonLat = this.toLonLat(coords);
      this.onTextPlacement?.(lonLat as [number, number]);
    };

    this.map.on('singleclick', clickHandler);
    // Store reference for cleanup
    (this as any)._textClickHandler = clickHandler;
  }

  /**
   * Add a text annotation at the specified coordinates.
   */
  addTextAnnotation(coords: [number, number], text: string, textStyle?: TextStyle): Drawing {
    if (!this.drawingSource || !this.map) {
      throw new Error('DrawingToolService not initialized');
    }

    const style = textStyle || { ...this.currentTextStyle };
    const drawingId = generateId();
    const createdAt = Date.now();

    // Create point feature for text
    const projCoords = this.fromLonLat(coords);
    const feature = new Feature({
      geometry: new Point(projCoords),
    });
    feature.setId(drawingId);
    feature.set('drawingType', 'text');
    feature.set('drawingId', drawingId);
    feature.set('drawingStyle', { ...this.currentStyle });
    feature.set('textContent', text);
    feature.set('textStyle', style);
    feature.set('createdAt', createdAt);

    this.drawingSource.addFeature(feature);

    const drawing: Drawing = {
      id: drawingId,
      type: 'text',
      geometry: { type: 'Point', coordinates: coords },
      style: { ...this.currentStyle },
      text,
      textStyle: style,
      createdAt,
    };

    return drawing;
  }

  // ─── Editing Interactions ────────────────────────────────────────────────

  /**
   * Enable modify interaction for moving/resizing drawings.
   * Requirement 39.5: move, resize, delete.
   */
  enableModify(): void {
    if (!this.map || !this.drawingSource) return;

    this.deactivateAllInteractions();

    this.modifyInteraction = new Modify({
      source: this.drawingSource,
    });
    (this.modifyInteraction as any).on?.('modifyend', (event: any) => {
      for (const feature of event.features.getArray()) {
        const drawing = this.featureToDrawing(feature as Feature);
        if (drawing) this.onDrawingChanged?.(drawing);
      }
    });
    this.map.addInteraction(this.modifyInteraction);

    this.translateInteraction = new Translate({
      layers: [this.drawingLayer!],
    });
    (this.translateInteraction as any).on?.('translateend', (event: any) => {
      for (const feature of event.features.getArray()) {
        const drawing = this.featureToDrawing(feature as Feature);
        if (drawing) this.onDrawingChanged?.(drawing);
      }
    });
    this.map.addInteraction(this.translateInteraction);
  }

  /** Remove Draw/Modify/Translate ownership without disposing the drawing layer. */
  deactivateInteractions(): void {
    this.deactivateAllInteractions();
  }

  /**
   * Delete a drawing by its ID.
   */
  deleteDrawingById(drawingId: string): void {
    if (!this.drawingSource) return;

    const feature = this.drawingSource.getFeatureById(drawingId);
    if (feature) {
      this.drawingSource.removeFeature(feature);
    }
  }

  /**
   * Update text content of an existing text annotation.
   */
  updateTextAnnotation(drawingId: string, text: string, textStyle?: TextStyle): void {
    if (!this.drawingSource) return;

    const feature = this.drawingSource.getFeatureById(drawingId);
    if (feature) {
      feature.set('textContent', text);
      if (textStyle) {
        feature.set('textStyle', textStyle);
      }
      feature.changed();
    }
  }

  // ─── Clear and Save ──────────────────────────────────────────────────────

  /**
   * Clear all drawings from the temporary layer.
   * Requirement 39.6: clear all drawings.
   */
  clearAll(): void {
    this.drawingSource?.clear();
  }

  /**
   * Get all drawings as GeoJSON features for saving as a vector layer.
   * Requirement 39.6, 40.6: save as vector layer.
   */
  getDrawingsAsGeoJSON(): FeatureCollection {
    if (!this.drawingSource || !this.map) {
      return { type: 'FeatureCollection', features: [] };
    }

    const features = this.drawingSource.getFeatures();
    const geoJsonFeatures: GeoJSONFeature[] = features.map((feature) => {
      const geometry = feature.getGeometry();
      if (!geometry) {
        return {
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [0, 0] },
          properties: {},
        };
      }

      const geojsonGeometry = JSON.parse(
        this.geoJsonFormat.writeGeometry(geometry, {
          dataProjection: 'EPSG:4326',
          featureProjection: this.map!.getView().getProjection(),
        })
      );

      const properties: Record<string, any> = {
        drawingType: feature.get('drawingType'),
        drawingId: feature.get('drawingId'),
      };

      if (feature.get('textContent')) {
        properties.text = feature.get('textContent');
        properties.textStyle = feature.get('textStyle');
      }

      return {
        type: 'Feature' as const,
        geometry: geojsonGeometry,
        properties,
      };
    });

    return {
      type: 'FeatureCollection',
      features: geoJsonFeatures,
    };
  }

  /**
   * Load drawings from a list of Drawing objects (e.g., from Redux state).
   */
  loadDrawings(drawings: Drawing[]): void {
    if (!this.drawingSource || !this.map) return;

    this.drawingSource.clear();

    for (const drawing of drawings) {
      if (drawing.type === 'text') {
        const coords = (drawing.geometry as GeoJSONPoint).coordinates as [number, number];
        const projCoords = this.fromLonLat(coords);
        const feature = new Feature({
          geometry: new Point(projCoords),
        });
        feature.setId(drawing.id);
        feature.set('drawingType', 'text');
        feature.set('drawingId', drawing.id);
        feature.set('drawingStyle', drawing.style);
        feature.set('textContent', drawing.text || '');
        feature.set('textStyle', drawing.textStyle || this.currentTextStyle);
        feature.set('createdAt', drawing.createdAt);
        this.drawingSource.addFeature(feature);
      } else {
        const olGeometry = this.geoJsonFormat.readGeometry(JSON.stringify(drawing.geometry), {
          dataProjection: 'EPSG:4326',
          featureProjection: this.map.getView().getProjection(),
        });
        const feature = new Feature({ geometry: olGeometry });
        feature.setId(drawing.id);
        feature.set('drawingType', drawing.type);
        feature.set('drawingId', drawing.id);
        feature.set('drawingStyle', drawing.style);
        feature.set('createdAt', drawing.createdAt);
        this.drawingSource.addFeature(feature);
      }
    }
  }

  private featureToDrawing(feature: Feature): Drawing | null {
    if (!this.map) return null;

    const geometry = feature.getGeometry();
    const id = feature.get('drawingId') || feature.getId()?.toString();
    const type = feature.get('drawingType') as DrawingToolType;
    if (!geometry || !id || !type) return null;

    const drawing: Drawing = {
      id,
      type,
      geometry: JSON.parse(
        this.geoJsonFormat.writeGeometry(geometry, {
          dataProjection: 'EPSG:4326',
          featureProjection: this.map.getView().getProjection(),
        })
      ),
      style: feature.get('drawingStyle') || { ...this.currentStyle },
      createdAt: feature.get('createdAt') || Date.now(),
    };

    if (type === 'text') {
      drawing.text = feature.get('textContent') || '';
      drawing.textStyle = feature.get('textStyle') || { ...this.currentTextStyle };
    }

    return drawing;
  }

  // ─── Private Helpers ─────────────────────────────────────────────────────

  private deactivateAllInteractions(): void {
    if (!this.map) return;

    if (this.drawInteraction) {
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }
    if (this.modifyInteraction) {
      this.map.removeInteraction(this.modifyInteraction);
      this.modifyInteraction = null;
    }
    if (this.translateInteraction) {
      this.map.removeInteraction(this.translateInteraction);
      this.translateInteraction = null;
    }

    // Remove text click handler
    if ((this as any)._textClickHandler) {
      this.map.un('singleclick', (this as any)._textClickHandler);
      (this as any)._textClickHandler = null;
    }
  }

  private getDrawOptions(
    toolType: DrawingToolType
  ): {
    type: import('ol/geom/Geometry').default['getType'] | string;
    geometryFunction?: any;
  } | null {
    switch (toolType) {
      case 'point':
        return { type: 'Point' };
      case 'line':
        return { type: 'LineString' };
      case 'polygon':
        return { type: 'Polygon' };
      case 'circle':
        return { type: 'Circle' };
      case 'rectangle':
        return { type: 'Circle', geometryFunction: createBox() };
      default:
        return null;
    }
  }

  private getFeatureStyle(feature: Feature): Style | Style[] {
    const drawingType = feature.get('drawingType');
    const style = (feature.get('drawingStyle') as DrawingStyle) || this.currentStyle;

    if (drawingType === 'text') {
      const textContent = feature.get('textContent') || '';
      const textStyle = (feature.get('textStyle') as TextStyle) || this.currentTextStyle;

      return new Style({
        text: new OlText({
          text: textContent,
          font: `${textStyle.fontSize}px ${textStyle.font}`,
          fill: new Fill({ color: textStyle.fontColor }),
          backgroundFill: textStyle.backgroundColor
            ? new Fill({ color: textStyle.backgroundColor })
            : undefined,
          padding: [4, 6, 4, 6],
          offsetY: -10,
        }),
        image: new CircleStyle({
          radius: 4,
          fill: new Fill({ color: textStyle.fontColor }),
        }),
      });
    }

    // Shape styles
    const fillColor = this.hexToRgba(style.fillColor, style.opacity);
    const strokeColor = this.hexToRgba(style.strokeColor, 1);

    return new Style({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({
        color: strokeColor,
        width: style.strokeWidth,
      }),
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: fillColor }),
        stroke: new Stroke({
          color: strokeColor,
          width: style.strokeWidth,
        }),
      }),
    });
  }

  private hexToRgba(hex: string, opacity: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return `rgba(0, 0, 0, ${opacity})`;
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  private toLonLat(coords: number[]): [number, number] {
    if (!this.map) return [0, 0];
    const view = this.map.getView();
    const projection = view.getProjection();
    if (projection.getCode() === 'EPSG:4326') {
      return [coords[0], coords[1]];
    }
    // Simple Web Mercator to WGS84 conversion
    const x = coords[0];
    const y = coords[1];
    const lon = (x * 180) / 20037508.34;
    const lat = (Math.atan(Math.exp((y * Math.PI) / 20037508.34)) * 360) / Math.PI - 90;
    return [lon, lat];
  }

  private fromLonLat(coords: [number, number]): number[] {
    if (!this.map) return [0, 0];
    const view = this.map.getView();
    const projection = view.getProjection();
    if (projection.getCode() === 'EPSG:4326') {
      return coords;
    }
    // Simple WGS84 to Web Mercator conversion
    const [lon, lat] = coords;
    const x = (lon * 20037508.34) / 180;
    let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
    y = (y * 20037508.34) / 180;
    return [x, y];
  }
}

// Singleton instance
export const drawingToolService = new DrawingToolService();
